import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { TrendingUp, ArrowRight, Users } from 'lucide-react';
import { supabase } from '../../lib/supabase';

interface Campaign {
  id: string;
  name: string;
  target_amount: number;
  raised_amount: number;
  investor_count: number;
}

const FundraisingBanner: React.FC = () => {
  const [campaign, setCampaign] = useState<Campaign | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCampaign();
  }, []);

  const fetchCampaign = async () => {
    try {
      const { data } = await supabase
        .from('fundraising_campaigns')
        .select('id, name, target_amount, raised_amount, investor_count')
        .eq('is_active', true)
        .single();

      if (data) {
        setCampaign(data as Campaign);
      }
    } catch (error) {
      console.error('Error fetching campaign:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading || !campaign) return null;

  const percentage = Math.min((campaign.raised_amount / campaign.target_amount) * 100, 100);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  return (
    <section className="py-16 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 relative overflow-hidden">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_50%,_rgba(34,197,94,0.1)_0%,_transparent_50%)]"></div>
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_50%,_rgba(34,197,94,0.05)_0%,_transparent_50%)]"></div>

      <div className="container mx-auto px-4 md:px-6 relative">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-8">
            <div className="inline-flex items-center px-4 py-2 bg-primary-500/20 border border-primary-500/30 rounded-full text-sm font-medium text-primary-300 mb-4">
              <TrendingUp size={16} className="mr-2" />
              Levée de fonds en cours
            </div>

            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Proxy Africa ouvre son capital
            </h2>

            <p className="text-xl text-gray-300 mb-2">
              {formatCurrency(campaign.target_amount)}
            </p>
          </div>

          <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 md:p-8 border border-white/20">
            <div className="mb-6">
              <div className="flex justify-between text-sm mb-2">
                <span className="text-gray-300">Progression</span>
                <span className="text-white font-bold">{percentage.toFixed(1)}%</span>
              </div>

              <div className="w-full bg-gray-700 rounded-full h-3 overflow-hidden">
                <div
                  className="bg-gradient-to-r from-primary-400 to-primary-500 h-3 rounded-full transition-all duration-1000 ease-out relative overflow-hidden"
                  style={{ width: `${percentage}%` }}
                >
                  <div className="absolute inset-0 bg-white/20 animate-pulse"></div>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
              <div className="text-center p-3 bg-white/5 rounded-lg border border-white/10">
                <div className="text-2xl font-bold text-white mb-1">
                  {formatCurrency(campaign.raised_amount)}
                </div>
                <div className="text-xs text-gray-400">Montant levé</div>
              </div>

              <div className="text-center p-3 bg-white/5 rounded-lg border border-white/10">
                <div className="text-2xl font-bold text-white mb-1 flex items-center justify-center">
                  <Users size={20} className="mr-1" />
                  {campaign.investor_count}
                </div>
                <div className="text-xs text-gray-400">Investisseurs</div>
              </div>

              <div className="text-center p-3 bg-white/5 rounded-lg border border-white/10 col-span-2 md:col-span-1">
                <div className="text-2xl font-bold text-white mb-1">
                  {formatCurrency(campaign.target_amount)}
                </div>
                <div className="text-xs text-gray-400">Objectif</div>
              </div>
            </div>

            <div className="text-center">
              <Link
                to="/invest"
                className="inline-flex items-center px-8 py-4 bg-primary-600 hover:bg-primary-700 text-white font-semibold rounded-xl transition-all shadow-lg hover:shadow-xl transform hover:scale-105"
              >
                Découvrir l'opportunité
                <ArrowRight size={20} className="ml-2" />
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FundraisingBanner;
